import { ContactInfo } from '@weave/schema-gen-ts/dist/schemas/persons/v3/contact_info.pb';
import { PersonsV3 } from '@frontend/api-person';
import { formatPhoneNumber } from '@frontend/phone-numbers';

type DestinationLabel = {
  destination?: string;
  label?: string;
};

/**
 * This function is used to combine the labels of the destinations into a single string.
 * @param destinations - array of destination labels
 * @returns {string} - a string with the combined labels
 */

export const combineDestinationLabels = (destinations: DestinationLabel[]) =>
  destinations.map((destination) => destination.label).join('/');

/**
 * This function is the final formatted destination label. If the label is not provided, it will be empty.
 * @param obj - the destination and label to format
 * @returns {string} - a formatted string with the destination label and the phone number.
 */

export const formatDestinationLabel = ({ destination, label }: DestinationLabel) =>
  `${label ?? ''} ${formatPhoneNumber(destination)}`.trim();

/**
 * This function is used to get the matching destination from the array of destinations.
 * @param destinations - array of destination labels
 * @param match - the destination to match against
 * @returns {DestinationLabel[]} - an array of matching destinations
 */

export const getMatchingDestination = (destinations: DestinationLabel[] = [], match?: string) =>
  destinations.filter(({ destination }) => destination && match?.includes(destination));

/**
 * This function is used to format the destination label for the auto message suppression card.
 * @param contactInfo - contact info data should come from the SchemaPersonV3Service.GetPersonLegacy endpoint
 * @param originalDestination - the destination to match against
 * @returns {string} - a formatted string with the destination(s) label and the phone number.
 */

export const formatMatchingDestinations = (contactInfo: ContactInfo[] = [], originalDestination?: string) => {
  const destinations = contactInfo?.map(({ destination, type }) => ({
    destination,
    label: PersonsV3.PersonHelpers.contactTypeLabels[type],
  }));
  const matchingDestinations = getMatchingDestination(destinations, originalDestination);

  // no matches, so we will not specify the destination type
  if (!matchingDestinations.length) return formatDestinationLabel({ destination: originalDestination, label: '' });

  // combine the labels of the matching destinations
  const label = combineDestinationLabels(matchingDestinations);
  return formatDestinationLabel({ destination: originalDestination, label });
};
